import useFormattedDate from "@/dates/use-formatted-date";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@babackapp/customer-ui/components/alert";
import {
  ArticleImagesStack,
  ArticlesGroup,
  ArticlesGroupAction,
  ArticlesGroupContent,
  ArticlesGroupSubtitle,
  ArticlesGroupTitle,
} from "@babackapp/customer-ui/components/article";
import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
  ShopLogo,
} from "@babackapp/customer-ui/components/header";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { useReturnRequests } from "@babackapp/return-widget-components/queries/use-return-requests";
import { useReturnableItems } from "@babackapp/return-widget-components/hooks/use-returnable-items";
import { useCanOpenReturn } from "@babackapp/return-widget-components/hooks/use-can-open-return";
import type { SearchReturnRequestsResponseDto } from "@babackapp/private-api-client";
import { ExternalLink, InfoIcon, LoaderIcon } from "lucide-react";
import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useWidgetParams } from "../../hooks/use-widget-params";
import { useLineItemImages } from "../../hooks/use-line-item-images";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";

export function ReturnRequestsList() {
  const { orderId, shopSlug, isRetractionPortal, logoUrl } = useWidgetParams();
  const t = useCustomText();
  const returnableLineItems = useReturnableItems(orderId, shopSlug);
  const canOpenReturn = useCanOpenReturn();
  const { data: returnRequestsData } = useReturnRequests({ orderId, shopSlug });
  const navigate = useNavigate();

  const shouldRedirect =
    returnRequestsData && returnRequestsData.returnRequests.length === 0;

  useEffect(() => {
    if (shouldRedirect) {
      navigate(
        isRetractionPortal
          ? "../retractation-article-selection"
          : "../line-item",
        { replace: true },
      );
    }
  }, [shouldRedirect, isRetractionPortal, navigate]);

  if (!returnRequestsData || shouldRedirect) {
    return (
      <WidgetPage variant="centered">
        <LoaderIcon className="animate-spin text-muted-foreground size-5 mx-auto" />
      </WidgetPage>
    );
  }

  return (
    <WidgetPage>
      <Header>
        <ShopLogo src={logoUrl ?? undefined} />
        <HeaderTitle>{t("returnRequests.list.title")}</HeaderTitle>
        <HeaderDescription>
          {t("returnRequests.list.description")}
        </HeaderDescription>
      </Header>

      <ArticlesGroup
        className="cursor-pointer"
        layoutId="return-content"
        variants={{}}
      >
        {returnRequestsData?.returnRequests.map((returnRequest) => (
          <ReturnRequestLine
            returnRequest={returnRequest}
            key={returnRequest.id}
          />
        ))}
      </ArticlesGroup>

      {!canOpenReturn && (
        <Alert variant="info">
          <InfoIcon />
          <AlertTitle>
            {t("articleSelection.uniqueReturnRequest.title")}
          </AlertTitle>
          <AlertDescription>
            {t("articleSelection.uniqueReturnRequest.description")}
          </AlertDescription>
        </Alert>
      )}

      {canOpenReturn && !!returnableLineItems.length && (
        <AnimatedButtonRow variants={{}}>
          <AnimatedButtonContainer
            layoutId="line-item-confirm-button"
            variants={{}}
          >
            <Link
              to={
                isRetractionPortal
                  ? "../retractation-article-selection"
                  : "../line-item"
              }
            >
              <Button>{t("returnRequests.list.newReturn")}</Button>
            </Link>
          </AnimatedButtonContainer>
        </AnimatedButtonRow>
      )}
    </WidgetPage>
  );
}

type ReturnRequestLineProps = {
  returnRequest: SearchReturnRequestsResponseDto["returnRequests"][number];
};

function ReturnRequestLine({ returnRequest }: ReturnRequestLineProps) {
  const createdAt = useFormattedDate(returnRequest.createdAt);
  const lineItems = useLineItemImages(returnRequest);
  const t = useCustomText();

  return (
    <Link to={`./${returnRequest.id}`} key={returnRequest.id}>
      <ArticlesGroupContent variants={{}}>
        <ArticleImagesStack
          layoutIdPrefix={returnRequest.id}
          srcs={lineItems
            .map((li) => li.image)
            .filter((src): src is string => !!src)}
          ids={lineItems.map((li) => li.line_item_id)}
        />
        <ArticlesGroupTitle
          layoutId={returnRequest.id + "-title"}
          layout="position"
        >
          {t("returnRequests.item.title", { date: createdAt.formattedDate })}
        </ArticlesGroupTitle>
        <ArticlesGroupSubtitle
          layoutId={returnRequest.id + "-subtitle"}
          className="max-w-[340px]"
        >
          {t("returnRequests.item.subtitle", { count: lineItems.length })}
        </ArticlesGroupSubtitle>
        <ArticlesGroupAction layoutId={returnRequest.id + "action"}>
          <ExternalLink />
        </ArticlesGroupAction>
      </ArticlesGroupContent>
    </Link>
  );
}
